/**
 * DASHBOARD-TOPIC-HARASSMENT.WIDGET
 * Display harassment statistics by population
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Question } from "@/redux/question.types"
import { SurveyState } from "@/redux/survey.types"
import { useEffect, useState } from "react"
import PageLoader from "@/components/page-loader"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { store } from "@/core/root-reducer"
import { fetchQuestionHarassment } from "@/redux/_archive.actions"
import { FilterState } from "@/redux/filter.types"
import { Session } from "@/redux/_session.types"
import DashboardTopicHarassmentChipWidget from "./dashboard-topic-harassment-chip.widget"
import ListItem from "@/components/list-item"
import ListDropdown from "@/components/list-dropdown"
import Space from "@/components/space"
import { max } from "lodash"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser } from "@fortawesome/free-solid-svg-icons"

interface StateProps extends WithTranslation {
  _session: Session
  filter: FilterState
  survey: SurveyState
}

interface OwnProps {
  currentQuestion: Question
}

type Props = StateProps & OwnProps

function DashboardQuestionHarassmentWidget(props: Props) {
  const { t } = props

  //Filter active
  const [currentFilter, setCurrentFilter] = useState<string | null>(
    props._session.accountOptions.heatmapFilterName ?? null
  )
  const [isLoading, setIsLoading] = useState(true)
  const [populations, setPopulations] = useState<{ name: string; count: number; total: number }[]>([])
  const [maxValue, setMaxValue] = useState(0)

  useEffect(() => {
    load()
    async function load() {
      if (currentFilter) {
        const response: any = await store.dispatch(
          fetchQuestionHarassment(props.filter.dashboard, props.currentQuestion.aid as string, currentFilter)
        )
        if (!response.error) {
          setMaxValue(max(response.map((x) => x.count)) ?? 0)
          setPopulations(response.filter((x) => x.count > 0))
        }
      }

      setIsLoading(false)
    }
  }, [currentFilter])

  return (
    <PageLoader status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
      <div className="flex mb-20">
        <div className="grey-t">
          <b>{t("question_harassment_populations")}</b>
          <div className="text-sm mt-10">
            {t("question_harassment_populations_help", { count: props._session.participationMin })}
          </div>
        </div>
        <Space />
        <div>
          <ListDropdown
            isBold
            isCaretAlwaysVisible
            onSelect={(value) => setCurrentFilter(value.name)}
            value={currentFilter}
            values={props.filter.list.filter((x) => props._session.userExcludedFilters.indexOf(x.name) === -1)}
          />
        </div>
      </div>
      <div className="flex flex-wrap mb-[32px]">
        {populations.map((item, i) => (
          <div className="flex w-[50%]" key={i}>
            <ListItem>
              <DashboardTopicHarassmentChipWidget
                isOutlineColored={item.count !== maxValue}
                countHarassment={item.count}
              />
              <div className="flex align-center">
                <div className={item.count === maxValue ? "bold" : ""}>{item.name}</div>
                <div className="grey-t text-sm ml-10">
                  <FontAwesomeIcon icon={faUser} /> {item.total}
                  {i === 0 && ` ${t("question_harassment_participants")}`}
                </div>
              </div>
            </ListItem>
          </div>
        ))}
      </div>
    </PageLoader>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  filter: state.filter,
  survey: state.survey
})

export default connect(mapStateToProps)(withTranslation()(DashboardQuestionHarassmentWidget))
